import { readPref, writePref } from '@/lib/storage'

// How a board is shown (DESIGN.md "UI"): chat-main, where the conversation is
// the page and the canvas is a side panel that slides in when needed, or
// canvas-main, the whiteboard with the chat in a sidebar. Both can do the same
// things. Each board remembers its last layout, and in chat-main whether the
// side canvas was open. A board never opened before starts in chat-main; one
// used before chat-main existed starts where it was, in canvas-main.

export type Layout = 'chat' | 'canvas'
export const DEFAULT_LAYOUT: Layout = 'chat'

export function parseLayout(v: string | null): Layout | null {
  return v === 'chat' || v === 'canvas' ? v : null
}

/**
 * The layout a board opens in: the one remembered, else canvas-main for a
 * board this browser used before layouts existed (it has the older per-board
 * prefs: a collapsed sidebar, a saved camera, a chosen conversation), else
 * chat-main.
 */
export function initialLayout(stored: string | null, usedBefore: boolean): Layout {
  return parseLayout(stored) ?? (usedBefore ? 'canvas' : DEFAULT_LAYOUT)
}

/**
 * The layout to open a board in. Stored at once: later, the older prefs exist
 * for every board (a conversation gets chosen in chat-main too).
 */
export function readLayout(boardId: string): Layout {
  const stored = readPref(`layout:${boardId}`)
  const usedBefore = ['sidebar', 'viewport', 'session'].some(
    (k) => readPref(`${k}:${boardId}`) !== null,
  )
  const layout = initialLayout(stored, usedBefore)
  if (stored !== layout) writeLayout(boardId, layout)
  return layout
}

export function writeLayout(boardId: string, layout: Layout) {
  writePref(`layout:${boardId}`, layout)
}

/** The side canvas of chat-main: closed unless it was left open. */
export function readSideCanvas(boardId: string): boolean {
  return readPref(`side-canvas:${boardId}`) === 'open'
}

export function writeSideCanvas(boardId: string, open: boolean) {
  writePref(`side-canvas:${boardId}`, open ? 'open' : null)
}
